import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import ReactHotkeys from 'react-hot-keys';
import styled, { css } from 'styled-components';

import theme from '../assets/theme';
import { zIndexes } from '../utils/zIndexes';

const Portal = ({ children }: { children: React.ReactNode }) => {
  return typeof document === 'object' ? createPortal(children, document.body) : null;
};

const scaleFromCenter = {
  hidden: {
    opacity: 0,
    transform: 'scale(0)',
  },
  visible: {
    opacity: 1,
    transform: 'scale(1)',
    transition: {
      delay: 0.1,
      duration: 0.2,
    },
  },
  exit: {
    opacity: 0,
    transform: 'scale(0)',
  },
};

const maskOpacity = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
    },
  },
  exit: {
    opacity: 0,
  },
};

export const FullScreenSlidePreview = ({
  onClose,
  margin = 200,
  children,
}: {
  margin?: number;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  return (
    <Portal>
      <ReactHotkeys
        keyName={'Escape'}
        onKeyDown={(_, event) => {
          event.preventDefault();
          onClose();
        }}
      />
      <AnimatePresence>
        <Mask
          onClick={onClose}
          variants={maskOpacity}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <ResetHeightWidthContainer>
            <MaxSizeContainer $margin={margin}>
              <ResetHeightWidthContainer>
                <AspectRatioContainer
                  onClick={e => {
                    // Need this or else the mask onClick will be triggered
                    e.stopPropagation();
                  }}
                  $padding={20}
                  variants={scaleFromCenter}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {children}
                </AspectRatioContainer>
              </ResetHeightWidthContainer>
            </MaxSizeContainer>
          </ResetHeightWidthContainer>
        </Mask>
      </AnimatePresence>
    </Portal>
  );
};

const ResetHeightWidthContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: grid; // needed to center the content  vertically when you have a portrait window
`;

const AspectRatioContainer = styled(motion.div)<{ $padding: number }>`
  // Reset styles
  position: relative;
  display: block;
  aspect-ratio: 1920/1080; // added more height so that SlideWrapperViewer's broken resize observer actually is placed in the center of the box

  // handle scaling width / height
  // 1920 / 1200 was found by trial and error
  @media (max-aspect-ratio: 1920/1200) {
    // checks if we are on a wider screen and uses max width as fixed width where height increases based on aspect-ratio
    width: 100%;
    align-self: center;
  }
  // 1920 / 1200 was found by trial and error
  @media (min-aspect-ratio: 1920/1200) {
    // checks if we are on a taller screen and uses max height as fixed height where width increases based on aspect-ratio
    height: 100%;
    margin: auto; // used to center horisontally
  }

  // Styles
  border-radius: 10px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);

  ${({ $padding }) => css`
    /* Border radius and outline hack for safari <- works on all browsers */
    &::after {
      position: absolute;
      z-index: -1; // to allow click events in the children
      top: -${$padding}px;
      content: '';
      left: -${$padding}px;
      width: calc(100% + ${$padding * 2}px);
      height: calc(100% + ${$padding * 2}px);
      border: ${$padding}px solid ${theme.colors.background};
      border-radius: 10px;
      display: inherit;
    }
  `}
`;

const MaxSizeContainer = styled.div<{ $margin?: number }>`
  position: absolute;

  ${({ $margin }) =>
    $margin &&
    css`
      height: calc(100% - ${$margin}px);
      width: calc(100% - ${$margin}px);
      top: ${$margin}px;
      left: ${$margin}px;
      transform: translate(-${$margin / 2}px, -${$margin / 2}px);
    `}

  @media (max-width: 900px) {
    // 60 px => 10px margin due to outline hack
    height: calc(100% - 60px);
    width: calc(100% - 60px);
    top: 60px;
    left: 60px;
    transform: translate(-30px, -30px);
  }
`;

const Mask = styled(motion.div)`
  z-index: ${zIndexes('FULL_SIZE_MODAL')};
  position: fixed; // Prevent scrolling the modal mask
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;

  background: rgba(0, 0, 0, 0.6);
`;
